<template>
  <Header :geo="geo" />
  <ArticlePageSkeleton v-if="!data" />
  <template v-if="data">
    <SchemaOrg :data="data" />
    <article>
      <Wrap>
        <header class="post__head">
          <h1 v-html="articleDetails?.title" />
          <span
            class="post__tagline"
            v-html="articleDetails?.tagline"
          />
          <AuthorAndDate
            :author="articleAuthor"
            :published-at="articleDetails?.published_at"
            :updated-at="articleDetails?.updated_at"
          />
          <div class="img-container">
            <div
              v-if="isUgc"
              class="articles-list-reader-experience"
            >
              Опыт читателя
            </div>
            <img
              v-if="articleDetails?.main_photo?.scaled"
              :src="articleDetails?.main_photo?.scaled"
              class="post__cover"
              :alt="titleWithoutSpecSymbols"
            />
          </div>
        </header>
        <TableOfContents :contents-tokens="contentsTokens">
          <PostSearch
            :selected="selectedCity"
            :total-experiences-count="totalExperiencesCount"
            widget-location="top"
          />
          <div
            class="post__intro-text"
            v-html="articleDetails?.intro"
          />
        </TableOfContents>
      </Wrap>
      <Wrap>
        <div class="post-wrap">
          <Description
            :data="description"
            :guides="descriptionGuides"
            :article-title="titleWithoutSpecSymbols"
            :article-url="data?.seo?.canonical_url"
          />
        </div>
      </Wrap>
      <Wrap>
        <WriteBanner :article-id="articleDetails?.id" />
        <Review
          :review="review"
          @send-reaction="sendReaction"
          @send-review="sendReview"
        />
      </Wrap>
      <div class="guides-container">
        <GuidesContainerAdapter
          v-if="hasRelatedGuides"
          :guides="guidesSectionGuides"
          title="Узнать больше помогут гид"
          subtitle="Влюблены в место — и влюбляют других"
        />
      </div>
      <Wrap>
        <OtherPostList :posts="relatedArticles" />
        <ExperienceListingStandalone
          :experiences="experiencesWithFavorite"
          analytic-events-prefix="articles"
          :show-location="true"
          :more-button="showMoreButton"
          :more-button-loading="isLoadingExperiences"
          more-button-text="Показать еще"
          :skeletons-count="6"
          @load-more="handleLoadMore"
          @card-click="handleCardClick"
          @set-favorite="handleSetFavorite"
          @unset-favorite="handleUnsetFavorite"
        />
        <SubscriptionBlock
          api-url=""
          :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
        />
      </Wrap>
      <UiToast
        class="success-toast"
        icon-name="check_circle_icon"
        :open="isReviewSuccessToastOpen"
        @close="isReviewSuccessToastOpen = false"
      >
        <template #icon>
          <IconBase
            name="check_circle_icon"
            size="24"
          />
        </template>
        <template #content>Отзыв отправлен</template>
      </UiToast>
    </article>
  </template>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { computed, onBeforeMount } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { UiToast } from '@experience/core-toast-ui';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import AuthorAndDate from './components/AuthorAndDate.vue';
  import TableOfContents from './components/TableOfContents.vue';
  import PostSearch from './components/PostSearch.vue';
  import Review from './components/Review.vue';
  import Header from './components/Header.vue';
  import OtherPostList from './components/OtherPostList.vue';
  import SchemaOrg from './components/SchemaOrg.vue';
  import GuidesContainerAdapter from './components/GuidesContainerAdapter.vue';
  import ArticlePageSkeleton from './components/ArticlePageSkeleton.vue';
  import Description from './components/Description/Description.vue';
  import WriteBanner from './components/WriteBanner.vue';
  import Wrap from '@/shared/ui/Wrap.vue';
  import IconBase from '@/shared/ui/iconBase/IconBase.vue';
  import { MF_NAME } from '@/app/const';
  import ExperienceListingStandalone from '@/features/ExperienceListingStandalone.vue';
  import { useExperienceEntity } from '@/entities/experience/entity';
  import { useArticleHashScroll } from './composables/useArticleHashScroll';
  import { useArticlePageData } from './composables/useArticlePageData';
  import { useArticleReview } from './composables/useArticleReview';
  import { useArticleExperiences } from './composables/useArticleExperiences';
  import { trackEvent } from '@/services/analytics';

  const { VITE_RUNTIME_SOGU_SERVICE_URL } = useRuntimeConfig()[MF_NAME];
  const route = useRoute();
  const router = useRouter();
  const experienceEntity = useExperienceEntity();

  const {
    data,
    getArticle,
    isUgc,
    titleWithoutSpecSymbols,
    contentsTokens,
    articleAuthor,
    geo,
    selectedCity,
    descriptionTokens,
    descriptionGuides,
    guidesSectionGuides,
    relatedArticles,
    hasRelatedGuides,
    totalExperiencesCount,
  } = useArticlePageData(route, router);

  useArticleHashScroll(route, data);

  const { review, isReviewSuccessToastOpen, getReview, sendReaction, sendReview } = useArticleReview(data);

  const {
    experiencesWithFavorite,
    isLoadingExperiences,
    showMoreButton,
    handleLoadMore,
    handleSetFavorite,
    handleUnsetFavorite,
    handleCardClick,
  } = useArticleExperiences(data);

  const articleDetails = computed(() => data.value?.article_details);

  const description = computed(() => ({
    id: articleDetails.value?.id ?? 0,
    blocks: descriptionTokens.value,
  }));

  useHead(() => {
    const seo = data.value?.seo;
    return {
      title: seo?.title,
      link: seo?.canonical_url ? [{ rel: 'canonical', href: seo.canonical_url }] : [],
      meta: seo?.description ? [{ name: 'description', content: seo.description }] : [],
    };
  });

  onBeforeMount(async () => {
    await getArticle();
    getReview();
    experienceEntity.loadFavorites();
    trackEvent('journal:article.pageview', { slug: String(route.params.slug) });
  });
</script>

<style lang="scss" scoped>
  @import '@/app/styles/post_page';

  .post__cover {
    display: block;
    width: 100%;
    margin: var(--alias-space-32) 0 0;

    @include tablet {
      margin-top: var(--alias-space-24);
    }

    @include mobile {
      margin-top: var(--alias-space-20);
    }
  }

  .guides-container {
    margin-top: 80px;

    @include tablet {
      margin-top: 56px;
    }

    @include mobile {
      margin-top: 48px;
    }
  }

  .img-container {
    position: relative;
  }

  .articles-list-reader-experience {
    position: absolute;
    top: 0;
    left: 0;
    padding: 8px 24px;
    border-radius: 0 0 16px;
    background-color: var(--alias-color-background-accent-orange);
    color: var(--alias-color-text-inverse);

    @include base-300-300-semi;

    @include mobile {
      padding: 6px 12px;

      @include base-200-200-medium;
    }
  }
</style>
